import type { ProjectSnapshot } from '@/model/sourceState/sourcestate.generated';

import { unwrapEnvelopeOr } from '@/model/client';
import {
  sourceStateApi,
  useGetApiProjectsByProjectIdSourceStatesQuery,
} from '@/model/sourceState/api.generated';
import { skipToken } from '@reduxjs/toolkit/query/react';

const POLL_ACTIVE_MS = 3_000;
const POLL_IDLE_MS = 30_000;

function isActive(snapshot: ProjectSnapshot | undefined): boolean {
  return (snapshot?.sources ?? []).some(
    (source) =>
      source.state.status === 'syncing' ||
      source.tables.some((table) => table.status === 'syncing') ||
      source.result_tables.some((table) => table.status === 'syncing'),
  );
}

export function useProjectSourceStates(
  projectId: string | undefined,
  liveUpdates: boolean,
  awaitingRun = false,
): { snapshot: ProjectSnapshot | undefined } {
  const arg = projectId ? { projectId } : skipToken;
  const { data: cached } =
    sourceStateApi.endpoints.getApiProjectsByProjectIdSourceStates.useQueryState(arg);
  const active = isActive(unwrapEnvelopeOr<ProjectSnapshot | undefined>(cached, undefined));
  const pollingInterval = liveUpdates ? 0 : active || awaitingRun ? POLL_ACTIVE_MS : POLL_IDLE_MS;
  const query = useGetApiProjectsByProjectIdSourceStatesQuery(arg, {
    pollingInterval,
    skipPollingIfUnfocused: true,
  });

  return { snapshot: unwrapEnvelopeOr<ProjectSnapshot | undefined>(query.data, undefined) };
}
